import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import { ShieldCheck, AlertTriangle, Clock, CheckCircle2, Loader2, ExternalLink, Eye, XCircle, Upload } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CLS = {
  complete:       'bg-green-100 text-green-800 border-green-200',
  pending_review: 'bg-amber-100 text-amber-800 border-amber-200',
  incomplete:     'bg-red-100 text-red-800 border-red-200',
};

const USER_TYPE_CLS = {
  investor: 'bg-slate-100 text-slate-700 border-slate-200',
  client:   'bg-blue-100 text-blue-700 border-blue-200',
};

const KYC_DOC_TYPES = [
  { key: 'id_doc', label: 'ID / Passport' },
  { key: 'proof_of_address', label: 'Proof of address' },
  { key: 'bank_letter', label: 'Bank confirmation letter' },
  { key: 'tax_clearance', label: 'Tax clearance' },
  { key: 'company_reg', label: 'Company registration (CIPC)' },
  { key: 'authorised_signatory', label: 'Authorised signatory resolution' },
  { key: 'trust_deed', label: 'Trust deed' },
  { key: 'source_of_funds', label: 'Source of funds' },
  { key: 'other', label: 'Other' },
];

export default function CompliancePage() {
  const { token, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, complete: 0, incomplete: 0, pending_review: 0 });
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploadDocType, setUploadDocType] = useState('id_doc');
  const [uploading, setUploading] = useState(false);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      setLoading(true);
      const params = filter === 'all' ? '' : `?status_filter=${filter}`;
      const r = await axios.get(`${API_URL}/api/compliance/kyc-dashboard${params}`, authHeader);
      setRows(r.data.items || []);
      setSummary(r.data.summary || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load KYC dashboard');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, token]);

  const openInvestor = async (row) => {
    setSelectedUser(row);
    setDocsLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/users/${row.user_id}/kyc-documents`, authHeader);
      setDocs(r.data.documents || []);
    } catch (e) {
      toast.error('Failed to load documents');
    } finally { setDocsLoading(false); }
  };

  const review = async (doc, status, notes = null) => {
    try {
      await axios.put(
        `${API_URL}/api/compliance/kyc-documents/${doc.id}/review`,
        { status, notes: notes || null },
        authHeader
      );
      toast.success(`Document ${status} — investor has been notified by email.`);
      await openInvestor(selectedUser);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const viewDocument = async (doc) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/kyc/documents/${doc.id}/download`,
        { ...authHeader, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' })
      );
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Failed to open document');
    }
  };

  const uploadDocument = async (file) => {
    if (!file || !selectedUser) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(
        `${API_URL}/api/admin/users/${selectedUser.user_id}/kyc-documents?document_type=${encodeURIComponent(uploadDocType)}`,
        fd,
        { headers: { ...authHeader.headers, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Document uploaded');
      await openInvestor(selectedUser);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const promptRejection = (doc) => {
    const reason = window.prompt('Reason for rejection (shown to the investor):');
    if (reason === null) return; // cancelled
    if (!reason.trim()) {
      toast.error('A rejection reason is required.');
      return;
    }
    review(doc, 'rejected', reason.trim());
  };

  return (
    <div className="p-6 space-y-6" data-testid="compliance-page">
      <div>
        <h1 className="text-2xl font-bold text-[#1D2D2A] font-['Manrope']">KYC Compliance Dashboard</h1>
        <p className="text-[#6C757D] mt-1">Signed in as <span className="font-medium">{user?.email}</span> · Reviewer</p>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <SummaryTile label="Total investors & clients" value={summary.total} icon={<ShieldCheck className="h-5 w-5 text-slate-600" />} />
        <SummaryTile label="KYC complete"    value={summary.complete}       icon={<CheckCircle2 className="h-5 w-5 text-green-600" />} colour="green" />
        <SummaryTile label="Pending review"  value={summary.pending_review} icon={<Clock className="h-5 w-5 text-amber-600" />}        colour="amber" />
        <SummaryTile label="Incomplete"      value={summary.incomplete}     icon={<AlertTriangle className="h-5 w-5 text-red-600" />}  colour="red" />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Investors</CardTitle>
            <CardDescription>Click a row to review that investor&apos;s uploaded KYC documents.</CardDescription>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-56" data-testid="compliance-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
              <SelectItem value="pending_review">Pending review</SelectItem>
              <SelectItem value="incomplete">Incomplete</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-[#6C757D]"><Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading…</div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-[#6C757D]">No investors match this filter.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>RP / ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead>Missing</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.user_id} data-testid={`compliance-row-${r.user_id}`}>
                    <TableCell>
                      <Badge className={`${USER_TYPE_CLS[r.user_type] || USER_TYPE_CLS.investor} border text-xs capitalize`}>
                        {r.user_type || 'investor'}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{r.rp_code || '—'}</TableCell>
                    <TableCell>{r.name}<div className="text-xs text-[#6C757D]">{r.email}</div></TableCell>
                    <TableCell>{r.entity_type}</TableCell>
                    <TableCell>{r.uploaded_count}</TableCell>
                    <TableCell className="text-xs">{r.missing_docs.length ? r.missing_docs.join(', ') : <span className="text-green-700">—</span>}</TableCell>
                    <TableCell><Badge className={`${STATUS_CLS[r.status]} border`}>{r.status.replace('_',' ')}</Badge></TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => openInvestor(r)} data-testid={`compliance-review-${r.user_id}`}>
                        Review <ExternalLink className="h-3 w-3 ml-1" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {selectedUser && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Documents — {selectedUser.name} ({selectedUser.rp_code})</CardTitle>
              <CardDescription>Upload a document on their behalf, or approve/reject what's already there.</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={uploadDocType} onValueChange={setUploadDocType}>
                <SelectTrigger className="w-56" data-testid="compliance-upload-doc-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KYC_DOC_TYPES.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <label
                className={`inline-flex items-center gap-1 px-3 py-2 rounded-md border cursor-pointer text-sm whitespace-nowrap ${uploading ? 'opacity-50 pointer-events-none' : 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'}`}
                data-testid="compliance-upload-btn"
              >
                <Upload className="h-4 w-4" />
                {uploading ? 'Uploading…' : 'Upload'}
                <input type="file" hidden onChange={e => { const f = e.target.files?.[0]; if (f) uploadDocument(f); e.target.value = ''; }} />
              </label>
              <Button variant="outline" size="sm" onClick={() => setSelectedUser(null)}>Close</Button>
            </div>
          </CardHeader>
          <CardContent>
            {docsLoading ? <div className="py-6 text-center text-[#6C757D]"><Loader2 className="inline h-5 w-5 animate-spin" /></div>
             : docs.length === 0 ? <div className="py-6 text-center text-[#6C757D]">No documents uploaded yet.</div>
             : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead><TableHead>File</TableHead><TableHead>Uploaded</TableHead>
                    <TableHead>Status</TableHead><TableHead /></TableRow>
                </TableHeader>
                <TableBody>
                  {docs.map(d => (
                    <TableRow key={d.id} data-testid={`compliance-doc-${d.id}`}>
                      <TableCell>{d.document_type}</TableCell>
                      <TableCell className="max-w-xs">
                        <button
                          type="button"
                          onClick={() => viewDocument(d)}
                          className="text-[#18A37C] hover:underline inline-flex items-center gap-1 text-left"
                          data-testid={`compliance-view-${d.id}`}
                          title="Open document in a new tab"
                        >
                          <Eye className="h-3 w-3" /> {d.file_name || '(file)'}
                        </button>
                      </TableCell>
                      <TableCell className="text-xs">{d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString('en-ZA') : '—'}</TableCell>
                      <TableCell>
                        <Badge className={
                          d.review_status === 'approved' ? 'bg-green-100 text-green-800 border-green-200 border'
                          : d.review_status === 'rejected' ? 'bg-red-100 text-red-800 border-red-200 border'
                          : 'bg-amber-100 text-amber-800 border-amber-200 border'
                        }>{d.review_status || 'pending'}</Badge>
                      </TableCell>
                      <TableCell className="space-x-2">
                        <Button size="sm" variant="outline" onClick={() => viewDocument(d)} data-testid={`compliance-view-btn-${d.id}`} title="View">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => review(d, 'approved')} data-testid={`compliance-approve-${d.id}`}>
                          <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-700 border-red-200" onClick={() => promptRejection(d)} data-testid={`compliance-reject-${d.id}`}>
                          <XCircle className="h-4 w-4 mr-1" /> Reject
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
             )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SummaryTile({ label, value, icon, colour }) {
  const bg = colour === 'green' ? 'bg-green-50' : colour === 'amber' ? 'bg-amber-50' : colour === 'red' ? 'bg-red-50' : 'bg-slate-50';
  return (
    <div className={`rounded-lg border p-4 ${bg}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#6C757D] uppercase tracking-wide">{label}</span>
        {icon}
      </div>
      <div className="mt-2 text-2xl font-bold text-[#1D2D2A]">{value}</div>
    </div>
  );
}
